import { useFormContext } from 'react-hook-form';
import { round, sumBy } from 'lodash';
import { EqualisationFormValues } from '../Editor/EqualiseActions/FormWrapper';
import { equalisations } from 'further-ui/domain';
import { AllocationRule } from 'further-types/lp/allocations';

type UseEqualiseRealisationAllocationsParams = {
  actionIndex: number;
  linkedActionIndex: number;
  allocationType:
    | 'originalRealisationAllocations'
    | 'newRealisationAllocations';
};

const useEqualiseRealisationAllocations = ({
  actionIndex,
  linkedActionIndex,
  allocationType,
}: UseEqualiseRealisationAllocationsParams) => {
  const { setValue, watch } = useFormContext<EqualisationFormValues>();

  const allocationsPath =
    `actions.${actionIndex}.linkedActions.${linkedActionIndex}.${allocationType}` as const;

  const allocationRulePath =
    `actions.${actionIndex}.linkedActions.${linkedActionIndex}.allocationRule` as const;

  const newRealisationAllocationTotalsPath =
    `actions.${actionIndex}.linkedActions.${linkedActionIndex}.newRealisationAllocationTotals` as const;

  const watchedFields = watch(allocationsPath);
  const watchedNewRealisationAllocationTotals = watch(
    newRealisationAllocationTotalsPath,
  );
  const parentActionFields = watch(`actions.${actionIndex}`);

  const {
    totalReceivedConsiderationLcy = 0,
    totalEarnoutLcy = 0,
    totalEscrowLcy = 0,
    totalWithholdingTaxLcy = 0,
    totalUnits = 0,
  } = watchedNewRealisationAllocationTotals || {};

  const liveTotalAllocationPercentage = sumBy(
    watchedFields,
    (field) => field.allocationPercentage,
  );

  const liveTotalUnitsAllocated = sumBy(
    watchedFields || [],
    (field) => field.unitsAllocated,
  );

  const liveTotalReceivedConsiderationLcy = sumBy(
    watchedFields || [],
    (field) => field.receivedConsiderationLcy,
  );

  const liveTotalEscrowConsiderationLcy = sumBy(
    watchedFields || [],
    (field) => field.escrowConsiderationLcy,
  );

  const liveTotalEarnoutConsiderationLcy = sumBy(
    watchedFields || [],
    (field) => field.earnoutConsiderationLcy,
  );

  const liveTotalWithholdingTaxPayableLcy = sumBy(
    watchedFields || [],
    (field) => field.withholdingTaxPayableLcy,
  );

  const redistributeWithholdingTax = () => {
    if (!watchedFields) return;

    const allocationsWithExclusions = watchedFields.map((allocation) => ({
      ...allocation,
      withholdingTaxExempt: allocation.withholdingTaxExempt,
    }));

    const recalculatedAllocations =
      equalisations.recalculateWitholdingTaxForRealisationAllocations(
        allocationsWithExclusions,
        { totalWithholdingTaxLcy },
      );

    recalculatedAllocations.forEach((allocation, idx) => {
      setValue(
        `${allocationsPath}.${idx}.withholdingTaxPayableLcy`,
        allocation.withholdingTaxPayableLcy,
      );
    });
  };

  const handleAllocationPercentageChange = (
    value: number,
    rowIndex: number,
  ) => {
    if (!watchedFields) return;

    const updatedAllocations = watchedFields.map((field, idx) =>
      idx === rowIndex ? { ...field, allocationPercentage: value } : field,
    );

    const newTotalAllocationPercentage = round(
      sumBy(updatedAllocations, 'allocationPercentage'),
      2,
    );

    let currentAllocations = updatedAllocations.map((field, idx) => {
      if (idx === rowIndex) {
        return {
          ...field,
          allocationPercentage: value,
          unitsAllocated: round(totalUnits * (value / 100), 2),
          receivedConsiderationLcy: round(
            totalReceivedConsiderationLcy * (value / 100),
            2,
          ),
          escrowConsiderationLcy: round(totalEscrowLcy * (value / 100), 2),
          earnoutConsiderationLcy: round(totalEarnoutLcy * (value / 100), 2),
          withholdingTaxPayableLcy: !field.withholdingTaxExempt
            ? round(totalWithholdingTaxLcy * (value / 100), 2)
            : field.withholdingTaxPayableLcy,
        };
      }
      return field;
    });

    if (newTotalAllocationPercentage === 100) {
      currentAllocations =
        equalisations.recalculateWitholdingTaxForRealisationAllocations(
          currentAllocations,
          {
            totalWithholdingTaxLcy,
          },
        );
      currentAllocations = equalisations.balanceRealisationAllocations({
        allocations: currentAllocations,
        totals: {
          totalReceivedConsiderationLcy,
          totalEarnoutLcy,
          totalEscrowLcy,
          totalWithholdingTaxLcy,
          totalUnits,
        },
      });
    }

    setValue(allocationRulePath, AllocationRule.Manual);

    currentAllocations.forEach((allocation, idx) => {
      setValue(
        `${allocationsPath}.${idx}.allocationPercentage`,
        allocation.allocationPercentage,
      );
      setValue(
        `${allocationsPath}.${idx}.unitsAllocated`,
        allocation.unitsAllocated,
      );
      setValue(
        `${allocationsPath}.${idx}.receivedConsiderationLcy`,
        allocation.receivedConsiderationLcy,
      );
      setValue(
        `${allocationsPath}.${idx}.escrowConsiderationLcy`,
        allocation.escrowConsiderationLcy,
      );
      setValue(
        `${allocationsPath}.${idx}.earnoutConsiderationLcy`,
        allocation.earnoutConsiderationLcy,
      );
      setValue(
        `${allocationsPath}.${idx}.withholdingTaxPayableLcy`,
        allocation.withholdingTaxPayableLcy,
      );
    });
  };

  const handleExcludeFromAllocationChange = (
    value: boolean,
    rowIndex: number,
  ) => {
    setValue(`${allocationsPath}.${rowIndex}.excludeFromAllocation`, value);
    setValue(allocationRulePath, AllocationRule.Manual);
    if (value) {
      setValue(`${allocationsPath}.${rowIndex}.allocationPercentage`, 0);
      setValue(`${allocationsPath}.${rowIndex}.unitsAllocated`, 0);
      setValue(`${allocationsPath}.${rowIndex}.receivedConsiderationLcy`, 0);
      setValue(`${allocationsPath}.${rowIndex}.escrowConsiderationLcy`, 0);
      setValue(`${allocationsPath}.${rowIndex}.earnoutConsiderationLcy`, 0);
      setValue(`${allocationsPath}.${rowIndex}.withholdingTaxPayableLcy`, 0);
      redistributeWithholdingTax();
    }
  };

  const handleExcludeFromWithholdingTaxChange = (
    value: boolean,
    rowIndex: number,
  ) => {
    setValue(`${allocationsPath}.${rowIndex}.withholdingTaxExempt`, value);
    redistributeWithholdingTax();
  };

  const handleAllocationRuleChange = (value: AllocationRule) => {
    setValue(allocationRulePath, value);

    if (value === AllocationRule.PerOriginalInvestment) {
      if (!watchedFields || watchedFields.length === 0) {
        return;
      }

      if (
        !watchedNewRealisationAllocationTotals ||
        !parentActionFields?.newAllocations
      ) {
        return;
      }

      const parentAllocations = parentActionFields.newAllocations;

      let newAllocations = watchedFields.map((realisationAlloc) => {
        const parentAlloc = parentAllocations.find(
          (alloc) => alloc.commitmentId === realisationAlloc.commitmentId,
        );

        const newPercentage =
          parentAlloc?.allocationPercentage ??
          realisationAlloc.allocationPercentage;
        const isExcluded = parentAlloc?.excludeFromAllocation ?? false;
        if (isExcluded) {
          return {
            ...realisationAlloc,
            allocationPercentage: newPercentage,
            unitsAllocated: 0,
            receivedConsiderationLcy: 0,
            escrowConsiderationLcy: 0,
            earnoutConsiderationLcy: 0,
            withholdingTaxPayableLcy: 0,
            excludeFromAllocation: true,
          };
        }

        return {
          ...realisationAlloc,
          allocationPercentage: newPercentage,
          unitsAllocated: round(totalUnits * (newPercentage / 100), 2),
          receivedConsiderationLcy: round(
            totalReceivedConsiderationLcy * (newPercentage / 100),
            2,
          ),
          escrowConsiderationLcy: round(
            totalEscrowLcy * (newPercentage / 100),
            2,
          ),
          earnoutConsiderationLcy: round(
            totalEarnoutLcy * (newPercentage / 100),
            2,
          ),
          withholdingTaxPayableLcy: !realisationAlloc.withholdingTaxExempt
            ? round(totalWithholdingTaxLcy * (newPercentage / 100), 2)
            : realisationAlloc.withholdingTaxPayableLcy,
          excludeFromAllocation: false,
        };
      });

      const totalPercentage = round(
        sumBy(
          parentAllocations.filter((alloc) => !alloc.excludeFromAllocation),
          'allocationPercentage',
        ),
        2,
      );

      if (totalPercentage === 100) {
        newAllocations =
          equalisations.recalculateWitholdingTaxForRealisationAllocations(
            newAllocations,
            {
              totalWithholdingTaxLcy,
            },
          );
        newAllocations = equalisations.balanceRealisationAllocations({
          allocations: newAllocations,
          totals: {
            totalReceivedConsiderationLcy,
            totalEarnoutLcy,
            totalEscrowLcy,
            totalWithholdingTaxLcy,
            totalUnits,
          },
        });
      }
      newAllocations.forEach((realisationAlloc, realisationIdx) => {
        setValue(
          `${allocationsPath}.${realisationIdx}.allocationPercentage`,
          realisationAlloc.allocationPercentage,
        );
        setValue(
          `${allocationsPath}.${realisationIdx}.unitsAllocated`,
          realisationAlloc.unitsAllocated,
        );
        setValue(
          `${allocationsPath}.${realisationIdx}.receivedConsiderationLcy`,
          realisationAlloc.receivedConsiderationLcy,
        );
        setValue(
          `${allocationsPath}.${realisationIdx}.escrowConsiderationLcy`,
          realisationAlloc.escrowConsiderationLcy,
        );
        setValue(
          `${allocationsPath}.${realisationIdx}.earnoutConsiderationLcy`,
          realisationAlloc.earnoutConsiderationLcy,
        );
        setValue(
          `${allocationsPath}.${realisationIdx}.withholdingTaxPayableLcy`,
          realisationAlloc.withholdingTaxPayableLcy,
        );
        setValue(
          `${allocationsPath}.${realisationIdx}.excludeFromAllocation`,
          realisationAlloc.excludeFromAllocation,
        );
      });
    }
  };

  return {
    allocationsPath,
    watchedFields,
    liveTotalAllocationPercentage,
    liveTotalUnitsAllocated,
    liveTotalReceivedConsiderationLcy,
    liveTotalEscrowConsiderationLcy,
    liveTotalEarnoutConsiderationLcy,
    liveTotalWithholdingTaxPayableLcy,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
    handleExcludeFromWithholdingTaxChange,
    handleAllocationRuleChange,
  };
};

export default useEqualiseRealisationAllocations;
